import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Section, Badge } from '../components/UI.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { getOptimizedImageUrl } from '../lib/utils.js';
import { X, ChevronLeft, ChevronRight, Play, Maximize2 } from 'lucide-react';

const Galleri = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/gallery_images');
        if (response.ok) {
          const data = await response.json();
          // Sort images so that the newly added ones are ordered nicely (newest first)
          const sortedData = data.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
          setGalleryImages(sortedData);
        }
      } catch (error) {
        console.error('Error fetching gallery images:', error);
      }
    };
    fetchImages();
  }, []);

  const isVideo = (url) => {
    if (!url || typeof url !== 'string') return false;
    const cleanUrl = url.split('?')[0].toLowerCase();
    return cleanUrl.endsWith('.mov') || cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm');
  };

  const openLightbox = (index) => {
    setActiveIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setActiveIndex(null);
    document.body.style.overflow = '';
  };

  const nextItem = (e) => {
    if (e) e.stopPropagation();
    if (galleryImages.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevItem = (e) => {
    if (e) e.stopPropagation();
    if (galleryImages.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextItem();
      if (e.key === 'ArrowLeft') prevItem();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, galleryImages]);

  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>Galleri &amp; Stemning fra Studiet | LK STUDIO</title>
        <meta name="description" content="Se billeder af vores lyse og moderne faciliteter på Frederiksberg. Oplev stemningen og udstyret til EMS træning og fysioterapi hos LK STUDIO." />
        <link rel="canonical" href="https://lkstudioems.com/galleri" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/galleri" />
        <meta property="og:title" content="Galleri &amp; Stemning fra Studiet | LK STUDIO" />
        <meta property="og:description" content="Se billeder af vores lyse og moderne faciliteter på Frederiksberg. Oplev stemningen og udstyret til EMS træning og fysioterapi hos LK STUDIO." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/ba1439dd-ae18-4207-b62d-ddee2e0294f6.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/galleri" />
        <meta name="twitter:title" content="Galleri &amp; Stemning fra Studiet | LK STUDIO" />
        <meta name="twitter:description" content="Se billeder af vores lyse og moderne faciliteter på Frederiksberg. Oplev stemningen og udstyret til EMS træning og fysioterapi hos LK STUDIO." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/ba1439dd-ae18-4207-b62d-ddee2e0294f6.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                Stemning & Faciliteter
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Vores <br />
                <span className="text-[#9A8E81] italic">Galleri</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    LK STUDIO EMS tilbyder personlig EMS træning i vores lyse og moderne faciliteter på Frederiksberg. Gå på opdagelse i vores studie her.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/img-8323.jpeg", { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="LK STUDIO Galleri Hero" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      <Section className="pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {galleryImages.length > 0 ? (
            galleryImages.map((item, index) => {
              const mediaUrl = item.image?.[0];
              const isMediaVideo = isVideo(mediaUrl);

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.05, 0.3) }}
                  viewport={{ once: true }}
                  onClick={() => openLightbox(index)}
                  className="aspect-square rounded-2xl overflow-hidden bg-[#CEBDAA]/20 group border border-[#CEBDAA]/10 cursor-pointer relative"
                >
                  {isMediaVideo ? (
                    <div className="w-full h-full relative">
                      <video 
                        src={mediaUrl} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        muted
                        loop
                        playsInline
                        autoPlay
                      />
                      <div className="absolute top-3 right-3 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-black/40 backdrop-blur-md text-white">
                        <Play className="w-3 h-3 fill-current ml-0.5" />
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={getOptimizedImageUrl(mediaUrl, { width: 400, height: 400, fit: 'cover' })} 
                      alt={item.alt_text || `LK STUDIO Galleri ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/90 text-[#1C1B1A] flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-all duration-300">
                      <Maximize2 className="w-4.5 h-4.5" />
                    </div>
                  </div>
                </motion.div>
              );
            })
          ) : (
            [...Array(8)].map((_, index) => (
              <div key={index} className="aspect-square rounded-2xl animate-pulse bg-[#CEBDAA]/10" />
            ))
          )}
        </div>
      </Section>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {activeIndex !== null && galleryImages[activeIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-10 select-none"
          >
            {/* Close Button */}
            <button 
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors p-2 z-50 cursor-pointer"
              aria-label="Luk galleri"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Prev Button */}
            <button 
              onClick={prevItem}
              className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors p-4 z-50 cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            {/* Next Button */}
            <button 
              onClick={nextItem}
              className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors p-4 z-50 cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Content Container */}
            <motion.div 
              key={activeIndex}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 120 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl max-h-[80vh] flex items-center justify-center"
            >
              {isVideo(galleryImages[activeIndex].image?.[0]) ? (
                <video 
                  src={galleryImages[activeIndex].image?.[0]} 
                  className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img 
                  src={galleryImages[activeIndex].image?.[0]} 
                  alt={galleryImages[activeIndex].alt_text || "LK STUDIO Galleri"}
                  className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl"
                />
              )}
            </motion.div>

            {/* Caption */}
            {galleryImages[activeIndex].alt_text && (
              <motion.p 
                key={`caption-${activeIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 text-white/80 text-center text-sm md:text-base font-light max-w-xl"
              >
                {galleryImages[activeIndex].alt_text}
              </motion.p>
            )}

            {/* Counter */}
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-xs tracking-widest">
              {activeIndex + 1} / {galleryImages.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Galleri;
